import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { supabase } from '../lib/supabase';
import { GALLERY_IMAGES } from '../constants';
import { GalleryImage } from '../types';
import { X, ZoomIn, ChevronRight, ChevronLeft, LayoutGrid, Sparkles, Loader2, Play } from 'lucide-react';
import RevealOnScroll from '../components/RevealOnScroll';

const LOCAL_STORAGE_KEY = 'palmal_gallery_local_backup';

const Gallery: React.FC = () => {
  const [filter, setFilter] = useState("All");
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);

  const categories = ["All", "Wedding", "Stage", "Party", "Corporate", "Catering", "Festival"];

  const filteredImages = filter === "All"
    ? images
    : images.filter(img => img.category === filter);

  useEffect(() => {
    fetchImages();
    window.scrollTo(0, 0);
  }, []);

  // Keyboard Navigation for Lightbox
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (lightboxIndex === null) return;

      switch (e.key) {
        case 'Escape':
          closeLightbox();
          break;
        case 'ArrowRight':
          navigateLightbox('next');
          break;
        case 'ArrowLeft':
          navigateLightbox('prev');
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex, filteredImages.length]);

  const fetchImages = async () => {
    setLoading(true);
    try {
      // 1. Fetch Local (Backup/Demo) - safely parsed
      let localImages: GalleryImage[] = [];
      try {
        const localData = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (localData) {
          localImages = JSON.parse(localData);
        }
      } catch (e) {
        console.warn("Failed to parse local gallery backup:", e);
        // If local storage is corrupt, clear it to prevent future crashes
        localStorage.removeItem(LOCAL_STORAGE_KEY);
      }

      // 2. Fetch Remote (Supabase)
      let remoteImages: GalleryImage[] = [];
      try {
        const { data, error } = await supabase
          .from('gallery_images')
          .select('*')
          .order('id', { ascending: false });

        if (error) {
          console.warn("Supabase backend unavailable or empty:", error.message);
        } else {
          remoteImages = data || [];
        }
      } catch (err) {
        console.warn("Supabase connection failed, using local mode only.");
      }

      // 3. Merge Images
      const imageMap = new Map<string | number, GalleryImage>();

      // Add Static Demo Images (Restored)
      GALLERY_IMAGES.forEach(img => imageMap.set(img.id, img));

      // Add remote first (Supabase)
      remoteImages.forEach(img => imageMap.set(img.id, img));

      // Add local (LocalStorage)
      localImages.forEach(img => imageMap.set(img.id, img));

      const combinedImages = Array.from(imageMap.values()).sort((a, b) => {
        const idA = Number(a.id);
        const idB = Number(b.id);

        // If both are numbers (uploaded), sort descending
        if (!isNaN(idA) && !isNaN(idB)) {
          return idB - idA;
        }
        // If mixed, keep mostly consistent
        return String(b.id).localeCompare(String(a.id));
      });

      setImages(combinedImages);
    } catch (error) {
      console.error('Unexpected error fetching images:', error);
    } finally {
      // Ensure loading spinner shows for at least a brief moment for smooth UX
      setTimeout(() => setLoading(false), 800);
    }
  };

  const handleFilterChange = (newFilter: string) => {
    if (newFilter === filter) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setFilter(newFilter);
      setIsTransitioning(false);
    }, 300);
  };

  // Lightbox Logic
  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setIsClosing(true);
    setTimeout(() => {
      setLightboxIndex(null);
      setIsClosing(false);
      document.body.style.overflow = '';
    }, 300);
  };

  const navigateLightbox = (direction: 'next' | 'prev') => {
    if (lightboxIndex === null) return;
    const total = filteredImages.length;
    if (direction === 'next') {
      setLightboxIndex((lightboxIndex + 1) % total);
    } else {
      setLightboxIndex((lightboxIndex - 1 + total) % total);
    }
  };

  const handleVideoHover = (e: React.MouseEvent<HTMLDivElement>, isEntering: boolean) => {
    const video = e.currentTarget.querySelector('video');
    if (!video) return;

    if (isEntering) {
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          // Prevent console error
        });
      }
    } else {
      video.pause();
      video.currentTime = 0;
    }
  };

  return (
    <>
      <SEO
        title="Event Portfolio Gallery"
        description="Explore breathtaking decorations from real weddings and events. See our artistic pandals, royal mandaps, and corporate setups."
        url="/gallery"
      />

      <div className="bg-maroon-950 pt-28 pb-12 md:pt-32 md:pb-20 text-center text-white relative overflow-hidden px-4">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(197,160,89,0.15),_transparent_70%)]"></div>
        <nav className="relative z-10 flex justify-center items-center text-xs md:text-sm mb-4 md:mb-6 uppercase tracking-widest font-bold">
          <Link to="/" className="text-gold-400 hover:text-white transition-colors">Home</Link>
          <ChevronRight className="w-3 h-3 md:w-4 md:h-4 mx-2 md:mx-3 text-gray-500" />
          <span className="text-white">Portfolio</span>
        </nav>
        <h1 className="text-3xl xs:text-4xl md:text-6xl lg:text-7xl font-serif font-black mb-3 md:mb-4 relative z-10 leading-tight">Our Masterpieces</h1>
        <p className="text-gold-400 uppercase tracking-[0.3em] md:tracking-[0.4em] text-[10px] md:text-sm font-light relative z-10">Capturing Moments of Grandeur</p>
      </div>

      <div className="bg-cream-50 min-h-screen py-8 md:py-20 px-3 md:px-4">
        <div className="container mx-auto max-w-[1600px]">
          {/* Filters */}
          <div className="flex flex-wrap justify-center gap-2 md:gap-4 lg:gap-6 mb-8 md:mb-20">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => handleFilterChange(cat)}
                className={`group relative px-4 py-2 md:px-10 md:py-3 lg:px-12 lg:py-4 rounded-full font-black text-[9px] md:text-xs lg:text-sm transition-all duration-500 border uppercase tracking-[0.1em] md:tracking-[0.15em] overflow-hidden ${filter === cat
                  ? "text-white border-transparent scale-105 md:scale-110 shadow-[0_10px_30px_rgba(43,2,2,0.3)]"
                  : "bg-white text-gray-500 border-gray-100 hover:border-gold-400 hover:text-maroon-900 hover:scale-105 hover:shadow-xl"
                  }`}
              >
                <div className={`absolute inset-0 bg-gradient-to-br from-maroon-900 to-maroon-800 transition-opacity duration-500 ${filter === cat ? 'opacity-100' : 'opacity-0'}`}></div>
                {filter === cat && (
                  <div className="absolute inset-0 w-[50%] h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-[200%] skew-x-[-25deg] animate-periodic-shine pointer-events-none"></div>
                )}
                <span className="relative z-10 flex items-center justify-center gap-2">
                  {filter === cat && <Sparkles className="w-3 h-3 md:w-4 md:h-4 text-gold-400 animate-pulse" />}
                  {cat}
                </span>
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center min-h-[50vh] gap-6 animate-fade-in">
              <div className="relative">
                {/* Decorative glow behind spinner */}
                <div className="absolute inset-0 bg-gold-200 rounded-full blur-xl animate-pulse opacity-60"></div>
                <Loader2 className="w-12 h-12 md:w-16 md:h-16 text-gold-600 animate-spin relative z-10" />
              </div>
              <div className="flex flex-col items-center gap-2">
                <p className="text-maroon-900 font-serif text-xl md:text-2xl animate-pulse font-medium tracking-wide">Gathering memories...</p>
                <p className="text-gold-500 text-xs uppercase tracking-[0.2em] font-bold">Please wait</p>
              </div>
            </div>
          ) : (
            <>
              {/* Masonry Layout */}
              <div className={`columns-1 md:columns-2 gap-8 space-y-8 mx-auto transition-all duration-500 ${isTransitioning ? 'opacity-0 translate-y-4 scale-95' : 'opacity-100 translate-y-0 scale-100'}`}>
                {filteredImages.map((img, idx) => (
                  <RevealOnScroll key={`${filter}-${img.id}`} delay={idx * 50} effect="fade-up" className="break-inside-avoid mb-8">
                    <div
                      className="relative group cursor-pointer overflow-hidden rounded-xl md:rounded-3xl shadow-xl hover:shadow-2xl bg-gray-200 transform transition-all hover:z-10"
                      onClick={() => openLightbox(idx)}
                      onMouseEnter={(e) => handleVideoHover(e, true)}
                      onMouseLeave={(e) => handleVideoHover(e, false)}
                    >
                      {img.type === 'video' ? (
                        <div className="relative w-full h-auto">
                          <video
                            src={img.url}
                            className="w-full h-auto block"
                            muted
                            loop
                            playsInline
                            preload="metadata"
                          />
                          <div className="absolute top-3 right-3 w-8 h-8 md:w-10 md:h-10 bg-black/40 backdrop-blur-md rounded-full flex items-center justify-center text-white/90 border border-white/10 pointer-events-none">
                            <Play className="w-3 h-3 md:w-4 md:h-4 fill-current ml-0.5" />
                          </div>
                          <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-colors pointer-events-none"></div>
                        </div>
                      ) : (
                        <img
                          src={img.url}
                          alt={img.alt}
                          className="w-full h-auto block transition-transform duration-1000 ease-in-out group-hover:scale-110"
                          loading="lazy"
                        />
                      )}

                      {/* Hover Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-maroon-950 via-maroon-950/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500 flex flex-col items-center justify-end p-6 md:p-10 text-center pointer-events-none">
                        <div className="transform translate-y-8 group-hover:translate-y-0 transition-all duration-500">
                          <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center mb-4 mx-auto text-gold-400">
                            {img.type === 'video' ? <Play className="w-6 h-6 md:w-8 md:h-8 fill-current ml-1" /> : <ZoomIn className="w-6 h-6 md:w-8 md:h-8" />}
                          </div>
                          <span className="text-gold-400 text-xs md:text-sm font-black uppercase tracking-[0.3em] block mb-2">{img.category}</span>
                          <p className="text-white font-serif text-lg md:text-2xl font-bold leading-tight">{img.alt}</p>
                        </div>
                      </div>
                    </div>
                  </RevealOnScroll>
                ))}
              </div>

              {filteredImages.length === 0 && !isTransitioning && (
                <div className="text-center py-20 md:py-40 animate-fade-in px-4">
                  <LayoutGrid className="w-12 h-12 md:w-20 md:h-20 text-gray-200 mx-auto mb-4 md:mb-6" />
                  <p className="text-gray-400 font-serif text-lg md:text-2xl italic">Gallery is empty.</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* FIXED LIGHTBOX */}
      {lightboxIndex !== null && filteredImages[lightboxIndex] && (
        <div
          className={`fixed inset-0 z-[100] bg-black/95 flex items-center justify-center transition-opacity duration-300 ${isClosing ? 'opacity-0' : 'opacity-100'}`}
          onClick={closeLightbox}
        >
          {/* Top Bar */}
          <div className="absolute top-0 left-0 right-0 p-4 md:p-6 flex justify-between items-center z-[120]">
            <div className="text-white/80 font-mono text-xs md:text-sm tracking-widest bg-white/10 px-4 py-1.5 rounded-full backdrop-blur-sm border border-white/5 pointer-events-auto select-none">
              {lightboxIndex + 1} / {filteredImages.length}
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
              className="pointer-events-auto text-white/70 hover:text-white transition-all bg-white/10 hover:bg-red-500/20 p-2.5 md:p-3 rounded-full hover:scale-110 active:scale-90 border border-white/5"
              aria-label="Close"
            >
              <X className="w-6 h-6 md:w-8 md:h-8" />
            </button>
          </div>

          {/* Nav Buttons */}
          <button
            onClick={(e) => { e.stopPropagation(); navigateLightbox('prev'); }}
            className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 p-2 md:p-3 text-white/50 hover:text-white hover:bg-white/10 rounded-full transition-all z-[120] hover:scale-110 active:scale-95 flex items-center justify-center"
            aria-label="Previous"
          >
            <ChevronLeft className="w-8 h-8 md:w-12 md:h-12" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); navigateLightbox('next'); }}
            className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 p-2 md:p-3 text-white/50 hover:text-white hover:bg-white/10 rounded-full transition-all z-[120] hover:scale-110 active:scale-95 flex items-center justify-center"
            aria-label="Next"
          >
            <ChevronRight className="w-8 h-8 md:w-12 md:h-12" />
          </button>

          {/* Main Content Area */}
          <div
            className={`relative flex items-center justify-center w-full h-full p-2 md:p-10 transition-transform duration-300 ease-out ${isClosing ? 'scale-95' : 'scale-100'}`}
          >
            {filteredImages[lightboxIndex].type === 'video' ? (
              <video
                key={filteredImages[lightboxIndex].url}
                src={filteredImages[lightboxIndex].url}
                controls
                autoPlay
                className="max-w-full max-h-[80vh] shadow-2xl focus:outline-none"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <img
                key={filteredImages[lightboxIndex].url}
                src={filteredImages[lightboxIndex].url}
                alt={filteredImages[lightboxIndex].alt}
                className="max-w-full max-h-[85vh] object-contain shadow-2xl select-none"
                onClick={(e) => e.stopPropagation()}
              />
            )}
          </div>

          {/* Bottom Caption Bar */}
          <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black via-black/80 to-transparent pt-12 z-[110] text-center pointer-events-none">
            <div className="pointer-events-auto inline-block">
              <h3 className="text-gold-400 font-serif text-lg md:text-xl font-bold tracking-wide mb-1">
                {filteredImages[lightboxIndex].category}
              </h3>
              <p className="text-gray-300 text-xs md:text-sm font-light tracking-wide opacity-90">
                {filteredImages[lightboxIndex].alt}
              </p>
            </div>
          </div>

          {/* Tap Zones for Mobile Navigation */}
          <div className="md:hidden absolute inset-y-0 left-0 w-[20%] z-[100]" onClick={(e) => { e.stopPropagation(); navigateLightbox('prev'); }}></div>
          <div className="md:hidden absolute inset-y-0 right-0 w-[20%] z-[100]" onClick={(e) => { e.stopPropagation(); navigateLightbox('next'); }}></div>
        </div>
      )}
    </>
  );
};

export default Gallery;